import { memo, useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem, Input } from './FillFlows.styled';
import FillTemplatesGrid from 'components/FillTemplates/Grid/Grid';
import FillTemplateDialog from 'components/Dialogs/FillTemplate/FillTemplate';
import FillTemplatesAutocomplete from 'components/FillTemplates/Autocomplete/Autocomplete';
import FillAnalysesList from 'components/Analyses/FillAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useFillFlows from 'hooks/useFillFlows';
import useFillAnalyses from 'hooks/useFillAnalyses';
import useFillTemplates from 'hooks/useFillTemplates';
import useBlob from 'hooks/useBlob';
import useChecksum from 'hooks/useChecksum';
import { DELETE_FILL_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { fillFlowsSelectors } from "features/fillFlows";
import { fillTemplatesSelectors } from 'features/fillTemplates';
import { blobsSelectors } from 'features/blobs';

const FillFlows = () => {
  const inputRef = useRef(null);
  const { openDeleteDialog, openFillTemplateDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getFillFlow, createFillFlow, updateFillFlow } = useFillFlows();
  const { getFillAnalyses, createFillAnalysis, clearLocalFillAnalyses } = useFillAnalyses();
  const { getFillTemplates, clearLocalFillTemplates } = useFillTemplates();
  const { processBlob } = useBlob();
  const { processFiles } = useChecksum();
  const id = useCustomSelector(fillFlowsSelectors.getFillFlowId);
  const fillFlow = useCustomSelector(fillFlowsSelectors.getFillFlowData);
  const flowFetching = useCustomSelector(fillFlowsSelectors.fillFlowIsFetching);
  const fillTemplatesFetching = useCustomSelector(fillTemplatesSelectors.fillTemplatesIsFetching);
  const blobFetching = useCustomSelector(blobsSelectors.blobIsFetching);
  const { pagination } = useCustomSelector(fillTemplatesSelectors.getFillTemplatesData);
  const [email, setEmail] = useState({ value: fillFlow?.email, error: '' });
  const [context, setContext] = useState({ value: fillFlow?.context, error: '' });
  const [fillTemplateId, setFillTemplateId] = useState({ value: '', formattedValue: null, error: '' });
  const [file, setFile] = useState({ value: null, formattedValue: null });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (fill_flow) => {
    setEmail({ value: fill_flow?.email, error: '' });
    setContext({ value: fill_flow?.context, error: '' });
  };
  const handleExited = () => {
    setEmail({ value: '', error: '' });
    setContext({ value: '', error: '' });
    setFillTemplateId({ value: '', formattedValue: null, error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;
    if (checkEmptyString(email.value)) {
      if (!checkValidEmail(email.value)) {
        setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
        hasError = true;
      }
    }
    if (hasError) return;

    const fill_flow = {
      ...(needToUpdateField(fillFlow?.email, email.value) && {  email: email.value }),
      ...(needToUpdateField(fillFlow?.context, context.value) && {  context: context.value }),
    };

    if (!_isEmpty(fill_flow)) {
      if (!!id) {
        updateFillFlow(fill_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createFillFlow(fill_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const fetchFillTemplates = useCallback((offset) => {
    getFillTemplates({ offset })
  }, [getFillTemplates]);
  const handleScroll = ({ target: { scrollTop, scrollHeight, offsetHeight } }) => 
  scrollTop >= scrollHeight - offsetHeight && pagination.total_count > pagination.count + pagination.offset && !fillTemplatesFetching && fetchFillTemplates(pagination.limit + pagination.offset);
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_FILL_FLOW });
  const handleUploadClick = () => inputRef.current.click();
  const handleFileChange = ({ target: { files } }) => {
    processFiles(files, ({ file, checksum }) =>
      processBlob({ file, checksum }, blob => { 
        setFile({ value: blob.id, formattedValue: file });
      })
    );
  };
  const handleCreateFillAnalysis = () => {
    const fill_analysis = { 
      input: file.value,
      fill_template_id: fillTemplateId.value
    };

    createFillAnalysis(fill_analysis)
    .then(({ error }) => {
      if (!error) {
        setFillTemplateId({ value: '', formattedValue: null, error: '' });
        setFile({ value: null, formattedValue: null });
      }
    })
  };
  const fetchFillAnalyses = useCallback(() => {
    getFillAnalyses({ offset: 0 })
  }, [getFillAnalyses]);

  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalFillAnalyses();
      clearLocalFillTemplates();
    };
  }, [id, clearLocalFillAnalyses, clearLocalFillTemplates]);
  useEffect(() => {
    if (firstTimeOpened && !id) {
      getFillFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { fill_flow } = payload;
          handleEntering(fill_flow);
          fetchFillTemplates(0);
          fetchFillAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }

  }, [getFillFlow, fetchFillTemplates, fetchFillAnalyses, firstTimeOpened, id]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <FillTemplateDialog />
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={flowFetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={flowFetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={flowFetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={flowFetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Email'
              disabled={flowFetching || (!!id && !edit)}
              value={email.value}
              onChange={({ target: { value } }) => setEmail({ value, error: '' })}
              helperText={email.error}
              error={!!email.error}
            />
            <TextField
              fullWidth
              label='Context'
              multiline
              minRows={3}
              maxRows={10}
              disabled={flowFetching || (!!id && !edit)}
              helperText={context.error}
              error={!!context.error}
              value={context.value}
              onChange={({ target: { value } }) => setContext({ value, error: '' })}
            />
          </CardItem>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Templates</Typography>
            <Button variant='outlined' disabled={flowFetching} onClick={openFillTemplateDialog}>Add</Button>
          </CardItem>
            <Container maxWidth={false} sx={{ overflow: 'auto', maxHeight: '150px' }} onScroll={handleScroll}>
              <FillTemplatesGrid {...{fetching: fillTemplatesFetching, onFetchTemplates: fetchFillTemplates }} />
            </Container>
          <CardItem>
          <Box display='flex' justifyContent='center' alignItems='center'> 
          </Box>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Input</Typography>
            <Button variant='outlined' disabled={flowFetching || blobFetching} onClick={handleCreateFillAnalysis}>Send</Button>
          </CardItem>
          <FillTemplatesAutocomplete
            id='templates'
            value={fillTemplateId.formattedValue}
            error={!!fillTemplateId.error}
            helperText={fillTemplateId.error}
            onChange={({ value, formattedValue, error }) => setFillTemplateId({ value, formattedValue, error })}
          />
          <Box display='flex' justifyContent='center' alignItems='center'>
            <Button 
              color='primary' 
              startIcon={<AddIcon />} 
              endIcon={blobFetching && <CircularProgress size={20} color='inherit' />}
              disabled={blobFetching || !id} 
              onClick={handleUploadClick}
            >
              PDF FILE
            </Button>
            {!!file.value && <Typography variant='body2' noWrap>{file?.formattedValue?.name}</Typography>}
            <Input
                ref={inputRef}
                accept=".pdf"
                id="documents"
                type="file"
                onClick={(event) => event.target.value = ''}
                onChange={handleFileChange}
            />
          </Box>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <FillAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <FillFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);